import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { KeyRound, Eye, EyeOff, Copy, Loader2, ShieldCheck, AlertTriangle, ScrollText, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { IconButton, ErrorState, describeError } from '@/components/primitives';
import { API, auth, domainOf } from './api';

function Credential({ record, index }) {
  const [shown, setShown] = useState(false);
  const site = record.site || 'Unknown site';
  const copyPassword = () => {
    if (!navigator.clipboard) { toast.error('Clipboard is not available in this browser'); return; }
    navigator.clipboard.writeText(record.password || '').then(
      () => toast.success('Password copied. Clear your clipboard when you are done.'),
      () => toast.error('Could not copy to the clipboard')
    );
  };
  return (
    <li className="ws-cred">
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="ws-cred-site ws-break">{site}</div>
        <div className="ws-cred-secret" data-masked={!shown} aria-live="polite">
          {shown ? record.password || '(empty)' : <><span aria-hidden="true">••••••••••</span><span className="sr-only">Password hidden</span></>}
        </div>
      </div>
      <IconButton
        label={shown ? `Hide password ${index + 1}` : `Reveal password ${index + 1}`}
        icon={shown ? EyeOff : Eye}
        pressed={shown}
        onClick={() => setShown((v) => !v)}
      />
      <IconButton label={`Copy password ${index + 1}`} icon={Copy} onClick={copyPassword} />
    </li>
  );
}

/**
 * Two steps so the audit-logged request is a deliberate act: the dialog
 * first explains the gate and the audit entry, and only "Retrieve" calls
 * GET /breach-results/{id}/password (which the backend logs as
 * breach_password_reveal, or breach_password_reveal_denied on a 403).
 * Passwords stay masked until revealed one by one.
 */
export default function RetrievePasswordDialog({ result, onOpenChange, onGoToDomains }) {
  const open = Boolean(result);
  const [phase, setPhase] = useState('confirm'); // confirm | loading | done | denied | error
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) { setPhase('confirm'); setData(null); setError(null); }
  }, [open, result?.id]);

  const retrieve = async () => {
    setPhase('loading');
    try {
      const res = await axios.get(`${API}/breach-results/${result.id}/password`, auth());
      setData(res.data);
      setPhase('done');
    } catch (err) {
      setError(err);
      setPhase(err.response?.status === 403 ? 'denied' : 'error');
    }
  };

  const domain = domainOf(result?.email);
  const creds = data?.credentials || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw-32px)] max-w-[560px] max-h-[calc(100dvh-32px)] overflow-y-auto rounded-lg bg-surface-2 text-ink"
        style={{ borderColor: 'var(--border-strong)' }}
        data-testid="password-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-left" style={{ fontSize: 'var(--fs-md)', lineHeight: 1.4 }}>
            <KeyRound size={16} aria-hidden="true" style={{ color: 'var(--state-critical)', flexShrink: 0 }} />
            Exposed credentials
          </DialogTitle>
          <DialogDescription className="text-left ws-dialog-sub">
            <span className="ws-mono ws-break" style={{ color: 'var(--text-primary)' }}>{result?.email}</span>
            {result?.breach_name && <> · {result.breach_name}</>}
            {phase === 'done' && <> · {data.count} matching record{data.count === 1 ? '' : 's'}</>}
          </DialogDescription>
        </DialogHeader>

        {(phase === 'confirm' || phase === 'loading') && (
          <div className="ws-stack" style={{ gap: 'var(--space-3)' }}>
            <div className="ws-note">
              <ShieldCheck aria-hidden="true" />
              <div>
                Plaintext passwords are released only for domains your organization has verified with a DNS TXT record.
                That keeps anyone from adding someone else's address and reading its credentials.
              </div>
            </div>
            <div className="ws-note">
              <ScrollText aria-hidden="true" />
              <div>
                Retrieving writes an entry to the IntelliSecure audit log with your account, <span className="ws-mono">{result?.email}</span> and
                this breach. Passwords stay masked until you reveal each one.
              </div>
            </div>
            <div className="ws-form-actions">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={phase === 'loading'}>Cancel</Button>
              <Button onClick={retrieve} disabled={phase === 'loading'} data-testid="password-retrieve">
                {phase === 'loading' ? <Loader2 className="is-spinner" aria-hidden="true" /> : <KeyRound aria-hidden="true" />}
                {phase === 'loading' ? 'Retrieving…' : 'Retrieve credentials'}
              </Button>
            </div>
          </div>
        )}

        {phase === 'denied' && (
          <div className="ws-stack" style={{ gap: 'var(--space-3)' }}>
            <div className="ws-note ws-note--warning" role="alert">
              <Lock aria-hidden="true" />
              <div>
                <strong>{domain || 'This domain'} is not verified.</strong> {error?.response?.data?.detail || describeError(error)}
                <div className="ws-hint" style={{ marginTop: 4 }}>The denied attempt was recorded in the audit log.</div>
              </div>
            </div>
            <div className="ws-form-actions">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
              <Button onClick={() => { onOpenChange(false); onGoToDomains(); }}>
                <ShieldCheck aria-hidden="true" /> Go to Domains
              </Button>
            </div>
          </div>
        )}

        {phase === 'error' && (
          <ErrorState variant="compact" title="Couldn't retrieve the credentials" error={error} onRetry={retrieve} />
        )}

        {phase === 'done' && (
          <div className="ws-stack" style={{ gap: 'var(--space-3)' }}>
            {creds.length > 0 ? (
              <div className="ws-note ws-note--critical" role="alert">
                <AlertTriangle aria-hidden="true" />
                <div>
                  <strong>Rotate this password now</strong> and anywhere it was reused. Treat these values as compromised.
                </div>
              </div>
            ) : (
              <div className="ws-note ws-note--warning">
                <AlertTriangle aria-hidden="true" />
                <div>
                  No plaintext password for this address is in the local breach dataset. The breach record says a password was
                  exposed, so rotating it is still recommended.
                </div>
              </div>
            )}
            {creds.length > 0 && (
              <ul className="ws-creds" aria-label="Exposed credentials" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {creds.map((c, i) => <Credential key={i} record={c} index={i} />)}
              </ul>
            )}
            <p className="ws-hint" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <ScrollText size={12} aria-hidden="true" /> This retrieval was recorded in the IntelliSecure audit log.
            </p>
            <div className="ws-form-actions">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Done</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
